import React from 'react';
import { ResponsiveBar, BarCanvas } from '@nivo/bar';
import {generateGradientsAndColors, generateGradients, applyGradients, formatLabel, NivoTextTheme} from "./utils";
import Tooltip from "./Tooltip";
import {CustomLabelsLayer} from "./Labels";


const getLegendHeightBySize = (size)=>{
    if (size === 'large'){
        return 25;
    }
    if (size === 'medium'){
        return 20;
    }
    if (size === 'small'){
        return 15;
    }
}

const getLegendSymbolSizeBySize = (size)=>{
    if (size === 'large'){
        return 16;
    }
    if (size === 'medium'){
        return 12;
    }
    if (size === 'small'){
        return 10;
    }
}


const WrappedAxisLabel = ({ value, x, y, textAnchor, rotation = 0, maxLength = 15, fontSize = 12, vertical = true,
                          verticalOffset=20}) => {
  const text = String(value || '');
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  words.forEach(word => {
    if ((currentLine + word).length <= maxLength) {
      currentLine += (currentLine ? ' ' : '') + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  });
  if (currentLine) lines.push(currentLine);

  const lineHeight = fontSize; // Line height matches font size for simplicity

  return (
    <g transform={`translate(${x},${y + (vertical ? verticalOffset : 0)})`}>
      {lines.map((line, i) => (
        <text
          key={i}
          textAnchor={textAnchor}
          transform={vertical ? `rotate(${rotation})` : `rotate(${rotation - 90}) translate(${-lineHeight * i}, 0)`}
          style={{ fontSize: `${fontSize}px`, fontWeight: '500', fontFamily: "var(--font-family)" }}
          dy={vertical ? i * lineHeight : 0}
        >
          {line}
        </text>
      ))}
    </g>
  );
};

/**
 *  *** IMPORTANT ***
 *      1) For single bars i.e a single bar for each data point, the mode should be 'stacked'
 *
 *
 * @param data {Array[object]}
 *              Single Bars Example
 *              [{id: "myBar", [<myBar>]: 50}, {id: "myBar2", [<myBar2>]: 30}]
 *
 *              Grouped/Stacked Bars Example
 *              [{id: '2020', ticker1Return: <0.3>, ticker2Return: <0.5>}, {id: '2021', ticker1Return: <0.4>, ticker2Return: <0.6>}]
 **
 *
 * @param keys {Array[string]} - The keys to use for the bars.
 *                               Single Bars Example
 *                               ['myBar', 'myBar2']
 *
 *                               Grouped/Stacked Bars Example
 *                               ['ticker1Return', 'ticker2Return']
 *
 *                               And the group/stack would be on the 'id' field so year here
 * @param customColors {object, optional} - Optional object to specify custom colors for the bars.
 * @param mode {string} - 'grouped' or 'stacked'
 * @param bottomAxisLabel
 * @param leftAxisLabel
 * @param rotateLabels
 * @param precision
 * @param postfix
 * @param vertical
 * @returns {JSX.Element}
 * @constructor
 */
const NivoBarChart = ({
                          data,
                          keys,
                          customColors,
                          bottomAxisLabel='',
                          leftAxisLabel='',
                          mode='grouped',
                          rotateLabels=0,
                          precision=1,
                          postfix='%',
                          vertical=true,
                          barPadding=0.5,
                          paddingBottom=50,
                          paddingLeft=60,
                          borderRadius=0,
                          gradientStep=80,
                          barOpacity=1,
                          legendSize='small',
                          showLegend=true,
                          labelFontSize=10

}) => {
    const { gradients, colorMapping } = generateGradientsAndColors(keys, customColors, gradientStep, barOpacity);
    const colorsToUse = customColors ? customColors : colorMapping;
    const theme = {...NivoTextTheme}
    theme.legends.text.fontSize = 10;
    theme.labels.text.fontSize = labelFontSize;
    theme.axis.ticks.text.fontSize = labelFontSize;
    const hasCustomLabels = data.some(d => d.customLabel);
    const layers = ['grid', 'axes', 'bars', 'markers', 'legends', 'annotations'];
    if (hasCustomLabels) {
        layers.push(({ bars }) => (
          <CustomLabelsLayer bars={bars} data={data} vertical={vertical} />
        ));
      }
    return (

    <ResponsiveBar
        theme={theme}
        layers={layers}
        data={data}
        keys={keys}
        indexBy="id"
        margin={{ top: 10, right: showLegend ? 130 : 20, bottom: paddingBottom, left: paddingLeft}}
        padding={barPadding}
        groupMode={mode}
        borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        layout={vertical ? 'vertical' : 'horizontal'}
        axisTop={null}
        axisRight={null}
        axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: !vertical ? 0 : rotateLabels,
            legend: bottomAxisLabel,
            legendPosition: 'middle',
            legendOffset: 15,
            truncateTickAt: 0,
            renderTick: (tick) => (
              <WrappedAxisLabel
                value={tick.value}
                x={tick.x}
                y={tick.y}
                textAnchor="middle"
                rotation={rotateLabels}
                fontSize={labelFontSize}
                vertical={vertical}
              />
            ),

        }}
        axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: vertical ? 0 : rotateLabels,
            legend: leftAxisLabel,
            legendPosition: 'middle',
            legendOffset: -40,
            renderTick: (tick) => (
              <WrappedAxisLabel
                value={tick.value}
                x={tick.x - 10} // Adjust this value to position the wrapped text
                y={tick.y}
                textAnchor="end"
                rotation={vertical ? 0 : rotateLabels}
                fontSize={labelFontSize}
                verticalOffset={0}
              />
            ),
        }}
        labelSkipWidth={12}
        labelSkipHeight={12}
        labelTextColor={'#DEE7EA'}
        label={(d)=>formatLabel(d, precision, postfix)} // Format labels as percentages
        legends={showLegend ? [
            {
                dataFrom: 'keys',
                anchor: 'top-right',
                direction: 'column',
                justify: false,
                translateX: 120,
                translateY: 0,
                itemsSpacing: 5,
                itemWidth: 100,
                itemHeight: getLegendHeightBySize(legendSize),
                symbolShape: 'circle',
                itemDirection: 'left-to-right',
                itemOpacity: 0.85,
                itemFontSize: 10,
                symbolSize: getLegendSymbolSizeBySize(legendSize),
                data: keys.map(key => ({
                    id: key,
                    label: key,
                    color: colorsToUse[key]
                    })
                ),
                effects: [
                    {
                        on: 'hover',
                        style: {
                            itemOpacity: 1
                        }
                    }
                ]
            }
        ] : []}
        defs={generateGradients(keys, colorsToUse, gradientStep, barOpacity)}
        fill={applyGradients(keys)}
        animate={true}
        motionConfig={'wobbly'}
        motionStiffness={90}
        motionDamping={15}
        borderRadius={borderRadius} // Adding rounding to the top of the bars
        tooltip={({ id, value, color }) => <Tooltip id={id} value={value} color={colorsToUse[id]} />}

    />
)};

export default NivoBarChart;
